<script setup>
import { useI18n } from 'vue-i18n'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'

// Shared public product card — used by the home ProductRail and the
// Rentals/Tours/Property listing grids so the card looks identical
// everywhere. The parent controls sizing (rail = fixed width flex item,
// grid = full cell); this component owns the card's look + content.
// Accent for hover/price comes from --card-accent (defaults to brand coral).
const props = defineProps({
  kind: { type: String, required: true }, // vehicle | tour | property
  item: { type: Object, required: true },
  href: { type: String, required: true },
})

const { t } = useI18n()

function title() {
  return props.kind === 'vehicle' ? `${props.item.make} ${props.item.model}`.trim() : props.item.title
}
function meta() {
  const it = props.item
  if (props.kind === 'vehicle') {
    const parts = []
    if (it.vehicle_class === 'two_wheeler') { if (it.engine_cc) parts.push(`${it.engine_cc}cc`) }
    else if (it.seats) parts.push(t('public.catalog.seats_count', { n: it.seats }))
    if (it.transmission) parts.push(t(`public.transmissions.${it.transmission}`))
    if (it.fuel_type)    parts.push(t(`public.fuels.${it.fuel_type}`))
    return parts.join(' · ')
  }
  if (props.kind === 'tour') {
    const parts = [t(`vendor_portal.tour.categories.${it.category}`)]
    if (it.duration_minutes) parts.push(`${Math.round(it.duration_minutes / 60)}h`)
    return parts.join(' · ')
  }
  const parts = [t(`host_portal.property.categories.${it.category}`)]
  if (it.area) parts.push(it.area)
  return parts.join(' · ')
}
function price() {
  if (props.kind === 'vehicle') return props.item.daily_rate
  if (props.kind === 'tour')    return props.item.price_adult
  return props.item.price_per_night || props.item.price_per_month
}
</script>

<template>
  <a :href="href" class="pcard">
    <div class="pcard__photo">
      <span v-if="item.verified" class="pcard__verified">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M12 3l7 3v5c0 4.5-3 7.6-7 9-4-1.4-7-4.5-7-9V6z" /><path d="M9 12l2 2 4-4" />
        </svg>{{ t('public.verified') }}
      </span>
      <img v-if="item.photo_url" :src="item.photo_url" :alt="title()" loading="lazy">
      <CatalogPlaceholder v-else :kind="kind" :category="item.category" />
    </div>
    <div class="pcard__body">
      <h3 class="pcard__title">{{ title() }}</h3>
      <p v-if="meta()" class="pcard__meta">{{ meta() }}</p>
      <p class="pcard__price">
        <span class="fw-value">{{ price() }}</span>
        <span v-if="kind === 'vehicle'" class="pcard__per"> {{ t('public.catalog.per_day') }}</span>
      </p>
    </div>
  </a>
</template>

<style scoped>
.pcard {
  display: block; width: 100%;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: 1rem; overflow: hidden; text-decoration: none; color: inherit;
  transition: transform 160ms ease, border-color 160ms ease, box-shadow 160ms ease;
}
.pcard:hover {
  transform: translateY(-3px);
  border-color: color-mix(in oklch, var(--card-accent, var(--accent)) 55%, var(--line));
  box-shadow: 0 16px 36px -22px color-mix(in oklch, var(--card-accent, var(--accent)) 70%, transparent);
}
.pcard__photo { position: relative; aspect-ratio: 4 / 3; background: var(--surfaceAlt); display: grid; place-items: center; overflow: hidden; }
.pcard__verified {
  position: absolute; top: 0.6rem; left: 0.6rem; z-index: 1;
  display: inline-flex; align-items: center; gap: 0.3rem;
  padding: 0.3rem 0.6rem 0.3rem 0.5rem; border-radius: 999px;
  background: var(--surface); color: var(--ink);
  font-size: 0.78rem; font-weight: 700; line-height: 1;
  box-shadow: 0 4px 12px -4px color-mix(in oklch, var(--ink) 40%, transparent);
}
.pcard__verified svg { width: 0.95rem; height: 0.95rem; color: var(--ok); }
.pcard__photo img { width: 100%; height: 100%; object-fit: cover; }
.pcard__body { padding: 0.9rem 1rem 1.05rem; }
.pcard__title { margin: 0 0 0.3rem; font-size: 1.05rem; font-weight: 700; color: var(--ink); }
.pcard__meta { margin: 0 0 0.5rem; font-size: 0.9rem; color: var(--inkSoft); }
.pcard__price { margin: 0; font-size: 1.15rem; color: var(--ink); }
.pcard__per { font-size: 0.85rem; color: var(--muted); }
</style>
